// Shared command-format registry.
//
// Single source of truth for the two-line tile format used by BOTH the
// Command Stack (organisms/ChildWindowsSidebar) and the HIST/FAVS panel
// (organisms/CommandHistoryPanel). Adding/changing a command here updates
// both surfaces.
//
// Line 1 = command label, Line 2 = command-specific structure description.

// Popup `command=` query value (or path) -> display command for line 1.
export const COMMAND_ALIASES = {
    plot: 'PLOT',
    plota: 'PLOTA',
    plotf: 'PLOTF',
    curve: 'CURVE',
    basis: 'BASIS',
    regress: 'REGRESS',
    regr: 'REGRESS',
    anchor: 'ANCHOR',
    thor: 'THOR',
    arc: 'ARC',
    stats: 'STATS',
    edge: 'EDGE',
    fwcm: 'FWCM',
    trace: 'TRACE',
    chronos: 'CHRONOS',
    ctascan: 'CTASCAN',
    omon: 'OMON',
    dslice: 'DSLICE',
    atlas: 'ATLAS',
    unwind: 'UNWIND',
    tear: 'UNWIND'
};

export const PATH_RULES = [
    [/\/unwind\b/i, 'UNWIND'],
    [/\/plotf\//i, 'PLOTF'],
    [/\/plot\//i, 'PLOT'],
    [/\/curve\//i, 'CURVE'],
    [/\/arc\//i, 'ARC'],
    [/\/edge\//i, 'EDGE'],
    [/\/fwcm\//i, 'FWCM'],
    [/\/chronos\b/i, 'CHRONOS'],
    [/\/stats\b/i, 'STATS'],
    [/\/atlas\b/i, 'ATLAS'],
    [/\/cta\b/i, 'CTASCAN'],
    [/\/omon\b/i, 'OMON'],
    [/\/dslice\b/i, 'DSLICE'],
    [/\/thor\b/i, 'THOR'],
    [/\/regress/i, 'REGRESS'],
    [/\/(fx-options|vol)\b/i, 'FXO'],
    [/\/equities\//i, 'EQUITY'],
    [/\/indexes\//i, 'INDEX'],
    [/\/irs\b/i, 'IRS'],
    [/\/currencies\b/i, 'CCY'],
    [/\/cds\b/i, 'CDS'],
    [/\/bonds?\b/i, 'BOND'],
    [/\/matchings?\b/i, 'MATCH'],
    [/\/trades?\b/i, 'TRADE'],
    [/\/watchlist\b/i, 'WATCHLIST'],
    [/\/quick-price/i, 'QUICK PRICE'],
    [/\/hoot\b/i, 'HOOT'],
    [/\/chat\b/i, 'CHAT']
];

// The commands captured into history: the 11 LHS rail commands plus UNWIND,
// which is captured but has no rail button. Line 1 on the HIST/FAVS surface
// must use the rail label exactly (e.g. REGR, not REGRESS).
export const RAIL_LABEL = {
    PLOT: 'PLOT',
    PLOTA: 'PLOTA',
    PLOTF: 'PLOTF',
    BASIS: 'BASIS',
    REGRESS: 'REGR',
    REGR: 'REGR',
    ANCHOR: 'ANCHOR',
    ARC: 'ARC',
    TRACE: 'TRACE',
    STATS: 'STATS',
    EDGE: 'EDGE',
    CTASCAN: 'CTASCAN',
    CTA: 'CTASCAN',
    // TEAR is an alias of UNWIND, so it must collapse to one pill / favourite key.
    UNWIND: 'UNWIND',
    TEAR: 'UNWIND'
};

// Appended, not inserted: CommandFilterPills shows only the first three
// collapsed, so adding at the end leaves the collapsed pill row unchanged.
export const IN_SCOPE_COMMANDS = [
    'PLOT', 'PLOTA', 'PLOTF', 'BASIS', 'REGR',
    'ANCHOR', 'ARC', 'TRACE', 'STATS', 'EDGE', 'CTASCAN', 'UNWIND'
];

export const parseUrl = (win) => {
    try {
        return new URL(win.url);
    } catch (e) {
        return null;
    }
};

export const cleanTitle = (title) => (title ? title.replace(' | Deon', '').trim() : '');

export const commandFromUrl = (win) => {
    const u = parseUrl(win);
    if (!u) return '';
    const cmd = (u.searchParams.get('command') || '').split('-')[0].toLowerCase();
    if (COMMAND_ALIASES[cmd]) return COMMAND_ALIASES[cmd];
    const rule = PATH_RULES.find(([re]) => re.test(u.pathname));
    return rule ? rule[1] : '';
};

// Line 1: prefer what the popup reported, else derive from the URL/title.
export const commandFor = (win) => win.command
    || commandFromUrl(win)
    || cleanTitle(win.title)
    || win.name
    || 'WINDOW';

export const lineTwoFromUrl = (win) => {
    const u = parseUrl(win);
    if (!u) return '';
    const legs = u.searchParams.get('legs');
    if (legs) {
        return legs
            .split(',')
            .map((l) => l.trim())
            .filter(Boolean)
            .join(', ');
    }
    return u.searchParams.get('ticker') || '';
};

export const subtitleFor = (win) => {
    if (win.line2) return win.line2;
    const t = cleanTitle(win.title);
    if (t && t !== 'Deon') return t;
    const fromUrl = lineTwoFromUrl(win);
    if (fromUrl) return fromUrl;
    const u = parseUrl(win);
    return u ? u.pathname : win.url || win.name || '';
};

// Which command a PLOT-page window/history entry represents, from its query.
export const derivePlotChildCommand = (query = {}, isPlotAdvance = false) => {
    if (query.plotType === 'immRoll') return 'ROLL';
    if (query.basis6s3sCurve) return '6s3s';
    if (query.bobCurve) return 'BOB';
    if (String(query.command || '').split('-')[0].toLowerCase() === 'basis') return 'BASIS';
    return isPlotAdvance ? 'PLOTA' : 'PLOT';
};

// Maps any internal command name to the LHS rail label used on tiles/pills.
export const toRailLabel = (command) => RAIL_LABEL[(command || '').toUpperCase()] || command;

export const isInScopeCommand = (command) => IN_SCOPE_COMMANDS.includes(toRailLabel(command));
